// `ApiError` → the feature's error vocabulary (handoff README §Error codes).
//
// Why this is not just `error.code`: `GUARDRAIL_VIOLATION` carries its reasons in
// `error.details.violations`, and `ApiError` only lifts `error.fields` into `fields` — everything
// else stays in the raw `payload`. So the violations are dug out here, once, instead of in each
// component. A 404 has no machine code at all (README's table leaves it blank), so it is mapped
// from the kind.
import {
  isPlatformActionErrorCode,
  type AdAccountCandidate,
  type DuplicateActionTarget,
  type PlatformActionError,
  type PlatformActionErrorCode,
} from '../models/platformActionError'

import { ApiError } from '@/shared/http'

/** The two codes that invalidate the previewed payload — the caller must propose again. */
const REPROPOSE_CODES = new Set<PlatformActionErrorCode>([
  'CONFIRM_TOKEN_EXPIRED',
  'CONFIRM_TOKEN_INVALID',
])

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** `payload.error.details.violations` — the guardrail reasons, as strings. */
export function readViolations(error: ApiError): string[] {
  const details = asRecord(asRecord(asRecord(error.payload)?.error)?.details)
  const violations = details?.violations
  if (!Array.isArray(violations)) return []
  return violations.filter((entry): entry is string => typeof entry === 'string')
}

/**
 * `payload.error.details.{request_id,status}` — the request a duplicate `idempotency_key` already
 * names. Absent by design when the collision is another workspace's row: the backend refuses
 * without disclosing it, so there is nothing for the caller to resume.
 */
export function readDuplicate(error: ApiError): DuplicateActionTarget | null {
  const details = asRecord(asRecord(asRecord(error.payload)?.error)?.details)
  const requestId = details?.request_id
  const status = details?.status
  if (typeof requestId !== 'string' || typeof status !== 'string') return null
  return { requestId, status }
}

/**
 * `payload.error.details.candidates` — the ad accounts an ambiguous create could have meant. Every
 * other `INVALID_ACTION_INPUT` (unknown platform, campaign not found) carries no details at all.
 */
export function readCandidates(error: ApiError): AdAccountCandidate[] {
  const details = asRecord(asRecord(asRecord(error.payload)?.error)?.details)
  const candidates = details?.candidates
  if (!Array.isArray(candidates)) return []
  return candidates.flatMap((entry) => {
    const row = asRecord(entry)
    const id = row?.ad_account_id
    const name = row?.name
    if (typeof id !== 'string') return []
    return [{ adAccountId: id, name: typeof name === 'string' ? name : id }]
  })
}

function resolveCode(error: ApiError): PlatformActionErrorCode {
  // Cancellation first, exactly as `ApiError.fromAxios` orders it: an aborted request is not a
  // failure and must never reach an error state or a toast.
  if (error.kind === 'canceled') return 'CANCELED'
  if (error.code && isPlatformActionErrorCode(error.code)) return error.code
  if (error.kind === 'notFound') return 'NOT_FOUND'
  return 'UNKNOWN'
}

export function classifyError(cause: unknown): PlatformActionError {
  const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  const code = resolveCode(error)

  return {
    code,
    requiresRepropose: REPROPOSE_CODES.has(code),
    violations: code === 'GUARDRAIL_VIOLATION' ? readViolations(error) : [],
    fields: error.fields,
    duplicate: code === 'DUPLICATE_ACTION_REQUEST' ? readDuplicate(error) : null,
    candidates: code === 'INVALID_ACTION_INPUT' ? readCandidates(error) : [],
    detail: error.detail,
  }
}

/** Page-level gate: the workspace is not whitelisted on the private flag. */
export function isDisabledError(error: PlatformActionError): boolean {
  return error.code === 'PLATFORM_ACTIONS_DISABLED'
}

/** Aborted by navigation/unmount — the caller returns without writing any state. */
export function isCanceled(error: PlatformActionError): boolean {
  return error.code === 'CANCELED'
}
